import { ProgramScopeType, ProgramScopedContentRow } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const PROGRAM_SCOPED_CONTENT_CACHE_VERSION = 'v5';

type ProgramScopedContentCachePayload = {
  rows: ProgramScopedContentRow[];
  savedAt: number;
  /** `count:max(updated_at)` — head 비교용 */
  contentVersion?: string;
};

function storageKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): string {
  return [
    'cp.programScopedContentRows',
    PROGRAM_SCOPED_CONTENT_CACHE_VERSION,
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scopeType,
    scopeKey,
  ].join('.');
}

function normalizeRows(raw: unknown): ProgramScopedContentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is ProgramScopedContentRow => !!row && typeof row === 'object');
}

/**
 * LS/학기 통합 캐시에 다른 학기 행이 섞인 경우 제거.
 * 스토리지 키는 학기별이지만 payload만 섞인 레거시·병합 버그 시 1학기 화면에 2학기 행만 남는 문제를 막는다.
 */
export function filterProgramScopedContentRowsByTermNumber(
  rows: ProgramScopedContentRow[],
  termNumber: 1 | 2
): ProgramScopedContentRow[] {
  if (!rows.length) return rows;
  return rows.filter((r) => {
    const tn = r.term_number;
    if (tn === 1 || tn === 2) return tn === termNumber;
    return true;
  });
}

export function loadProgramScopedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): { found: boolean; rows: ProgramScopedContentRow[]; contentVersion?: string } {
  if (!workspaceId || !gradeId || !scopeKey || typeof window === 'undefined') {
    return { found: false, rows: [] };
  }
  try {
    const raw = deviceCacheStorage.getItem(
      storageKey(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey)
    );
    if (!raw) return { found: false, rows: [] };
    const parsed = JSON.parse(raw) as ProgramScopedContentCachePayload | ProgramScopedContentRow[];
    if (Array.isArray(parsed)) {
      return {
        found: true,
        rows: normalizeRows(parsed),
        contentVersion: buildLearningContentScopeVersion(normalizeRows(parsed)),
      };
    }
    if (!parsed || typeof parsed !== 'object') return { found: false, rows: [] };
    const payload = parsed as ProgramScopedContentCachePayload;
    const rows = normalizeRows(payload.rows);
    return {
      found: true,
      rows,
      contentVersion:
        (payload.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows),
    };
  } catch {
    return { found: false, rows: [] };
  }
}

export function saveProgramScopedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string,
  rows: ProgramScopedContentRow[],
  options?: { contentVersion?: string }
): void {
  if (
    !workspaceId ||
    !gradeId ||
    !scopeKey ||
    typeof window === 'undefined' ||
    !canWriteSchoolScopedDataCache(workspaceId)
  ) {
    return;
  }
  try {
    const payload: ProgramScopedContentCachePayload = {
      rows,
      savedAt: Date.now(),
      contentVersion:
        (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows),
    };
    deviceCacheStorage.setItem(
      storageKey(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey),
      JSON.stringify(payload)
    );
  } catch {
    // ignore quota / private mode write errors
  }
}

export function clearProgramScopedContentRowsCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): void {
  if (!workspaceId || !gradeId || !scopeKey || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey));
  } catch {
    // ignore
  }
}

export function mergeProgramScopedContentRowsCacheRows(
  existingRows: ProgramScopedContentRow[],
  updatedRows: ProgramScopedContentRow[]
): ProgramScopedContentRow[] {
  if (updatedRows.length === 0) return existingRows;
  const updatedById = new Map<string, ProgramScopedContentRow>();
  updatedRows.forEach((row) => {
    if (row.id) updatedById.set(row.id, row);
  });
  const merged = existingRows.map((row) => updatedById.get(row.id) ?? row);
  const existingIdSet = new Set(existingRows.map((row) => row.id));
  updatedRows.forEach((row) => {
    if (!existingIdSet.has(row.id)) merged.push(row);
  });
  return merged;
}
